import {
  AuthStoreInterface,
  Type,
  evaluateWithContext,
  WithFields
} from "../index";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import { FormState } from "final-form";

export class Fields<
  T extends DynamicField = DynamicField,
  M extends Field = Field
> implements WithFields<T, M> {
  public fields: T[];
  public context?: AccessContext;
  public mapFields<R>(
    fn: (field: M, idx: number) => R,
    context?: Partial<AccessContext>
  ): R[] {
    const mergedCtx = mergeCtx(this.context, context);
    return this.fields
      .map((field: T, idx: number) => {
        return mapDynamicToStaticField(field, mergedCtx);
      })
      .filter((f: T) => f !== null)
      .map(fn);
  }

  public constructor(fields: T[], context?: AccessContext) {
    this.fields = withDefaults(fields);
    this.context = context;
  }
}

export const withDefaults = <T extends DynamicField = DynamicField>(
  fields: T[]
) => {
  return fields.map((field: T) => {
    return Object.assign({}, field, {
      title:
        field.title === undefined ? makeDefaultTitle(field) : makeTitle(field)
    });
  }) as T[];
};

const makeDefaultTitle = (field: DynamicField): string =>
  capitalize(startCase(field.name));
const makeTitle = (
  field: DynamicField
): FunWithCtxOr<string | false | undefined> => field.title || " ";

export function mergeCtx(
  ctx?: AccessContext,
  ctx2?: Partial<AccessContext>
): AccessContext {
  return Object.assign({}, ctx, ctx2) as AccessContext;
}

export function mapDynamicToStaticField<T extends DynamicField>(
  field: T,
  mergedCtx: AccessContext
) {
  let tmp: any = {};
  if (field.if && !field.if(mergedCtx)) {
    return null;
  }
  for (const key in field) {
    if (key !== "onChange" && key !== "onInit") {
      tmp[key] = evaluateWithContext(mergedCtx, field[key]);
    } else {
      tmp[key] = field[key];
    }
  }
  return tmp;
}

export interface Field {
  name: string;
  type: Type;
  readonly?: boolean;
  title?: false | string;
  group?: string;
  renderSize?: Size;
}

export type OnChangeCallback = (
  newValue: any,
  oldValue: any,
  formState: FormState<any>
) => void;

/**
 * Extension of Field class with optional dynamic setters.
 */
export interface DynamicField {
  name: string;
  type: FunWithCtxOr<Type>;
  readonly?: FunWithCtxOr<boolean | undefined>;
  title?: FunWithCtxOr<false | string | undefined>;
  group?: string;
  renderSize?: FunWithCtxOr<Size>;
  if?: (ctx: AccessContext) => boolean;
  onChange?: OnChangeCallback;
  onInit?: OnChangeCallback;
}

export type FunWithCtxOr<O> = O | ((ctx: AccessContext) => O);

/**
 * Execution context of mapFields method in WithFields interface
 *
 * It's a merge of data passed by mapFields method and predefined context stored in WithFields implementations
 */
export interface AccessContext {
  auth?: AuthStoreInterface;
  data?: any;
}

export enum Size {
  BIG,
  NORMAL
}
